import {
  createContext,
  useState,
  useContext,
  useEffect,
  type AriaAttributes,
  type ReactNode,
} from "react"
import { Link } from "react-router"
import { Icon } from "source/shared/components"

function useCrumbState() {
  return useState<ReactNode | null>(null)
}
const BreadcrumbNavigationContext = createContext<
  ReturnType<typeof useCrumbState>
>([null, () => {}])

export const BreadcrumbNavigationProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const crumbState = useCrumbState()
  return (
    <BreadcrumbNavigationContext.Provider value={crumbState}>
      {children}
    </BreadcrumbNavigationContext.Provider>
  )
}

export const BreadcrumbNavigationPortal = () => {
  const [crumbs, _setCrumbs] = useContext(BreadcrumbNavigationContext)

  return (
    crumbs && (
      <nav aria-label="Breadcrumb" className="breadcrumbs">
        <div className="breadcrumbs__layout">{crumbs}</div>
      </nav>
    )
  )
}

export const BreadcrumbNavigation = ({ children }: { children: ReactNode }) => {
  const [_crumbs, setCrumbs] = useContext(BreadcrumbNavigationContext)

  useEffect(() => {
    setCrumbs(children)
    return () => setCrumbs(null)
  }, [children])

  return <></>
}

export function BreadcrumbPage({
  linkUrl,
  linkState,
  pageText,
  isActive,
}: {
  linkUrl?: string
  linkState?: object
  pageText?: string
  isActive?: boolean
}) {
  let className: string
  let ariaCurrent: AriaAttributes["aria-current"]

  if (isActive) {
    className = "breadcrumbs__page breadcrumbs__current-page"
    ariaCurrent = "location"
  } else {
    className = "breadcrumbs__page"
    ariaCurrent = "false"
  }

  return (
    <>
      {linkUrl ? (
        <Link
          to={linkUrl}
          state={linkState}
          className={className}
          aria-current={ariaCurrent}
          aria-label={pageText}
        >
          <span className="text">{pageText}</span>
          <span className="icon">
            <Icon symbol="churchCenter#three-dots" />
          </span>
        </Link>
      ) : (
        <div className={className} aria-current={ariaCurrent}>
          {pageText}
        </div>
      )}
    </>
  )
}

export function BreadcrumbNavigationWithMe({
  children,
}: {
  children: ReactNode
}) {
  return (
    <BreadcrumbNavigation>
      <BreadcrumbPage linkUrl="/me" pageText="Me" />
      <BreadcrumbDividerIcon />
      {children}
    </BreadcrumbNavigation>
  )
}

export function BreadcrumbContent({
  isActive,
  children,
}: {
  isActive?: boolean
  children?: ReactNode
}) {
  let className: string
  let ariaCurrent: AriaAttributes["aria-current"]

  if (isActive) {
    className = "breadcrumbs__page breadcrumbs__current-page"
    ariaCurrent = "location"
  } else {
    className = "breadcrumbs__page"
    ariaCurrent = "false"
  }

  return (
    <div className={className} aria-current={ariaCurrent}>
      {children}
    </div>
  )
}

export function BreadcrumbDividerIcon() {
  return (
    <span className="breadcrumbs__divider-icon">
      <Icon symbol="general#right-chevron" aria-hidden />
    </span>
  )
}
